import { elements, type Element } from "@/data/elements";
import { getCategory, type GameCategoryKey } from "./categories";

/**
 * Pure functions for Element Battle move validation.
 *
 * Two modes:
 *  - "category": every named element must match the chosen category.
 *  - "last-letter": each name must start with the last letter of the
 *    previous name. The first move has no constraint.
 *
 * Both modes forbid repeats within a round.
 *
 * The engine is intentionally state-free at module level - callers pass
 * the round state in. This makes it trivial to unit-test and lets the
 * page own UI state via React.
 */

export type GameMode = "category" | "last-letter";

export type RoundConfig = {
  mode: GameMode;
  /** Required when mode === "category". */
  categoryKey?: GameCategoryKey;
};

export type MoveSuccess = {
  ok: true;
  element: Element;
  /** Canonical "Name (Symbol)" for display. */
  display: string;
  /**
   * If the input did not match exactly and was accepted via fuzzy matching,
   * this holds the user's original input so the UI can show
   * "Did you mean Helium? (accepted)".
   */
  correctedFrom?: string;
};

export type MoveFailure = {
  ok: false;
  reason:
    | "empty"
    | "unknown"
    | "repeat"
    | "wrong-category"
    | "wrong-letter";
  message: string;
};

export type MoveResult = MoveSuccess | MoveFailure;

// Build symbol & name lookup once. Names are normalized to lowercase, and
// "aluminium" / "aluminum" both resolve. Same for sulfur/sulphur, caesium/cesium.
const SPELLING_ALIASES: Record<string, string> = {
  aluminum: "aluminium",
  sulphur: "sulfur",
  cesium: "caesium",
};

const normalize = (raw: string): string =>
  raw
    .trim()
    .toLowerCase()
    // strip diacritics (e.g., Ørsted not relevant for element names but cheap)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const NAME_INDEX = new Map<string, Element>();
const SYMBOL_INDEX = new Map<string, Element>();
for (const el of elements) {
  NAME_INDEX.set(normalize(el.name), el);
  SYMBOL_INDEX.set(el.symbol.toLowerCase(), el);
}

/** Classic Levenshtein distance, capped early for speed. */
function levenshtein(a: string, b: string, max = 2): number {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const m = a.length;
  const n = b.length;
  const prev = new Array(n + 1);
  const curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > max) return max + 1;
    for (let j = 0; j <= n; j++) prev[j] = curr[j];
  }
  return prev[n];
}

/**
 * Find the nearest element by name within an edit-distance budget.
 * Budget scales with name length: short names (≤5) only tolerate 1 typo,
 * longer names tolerate up to 2. Returns null if nothing fits.
 */
export function fuzzyLookupElement(raw: string): Element | null {
  const n = normalize(raw);
  if (n.length < 3) return null; // too risky for very short input
  const budget = n.length <= 5 ? 1 : 2;
  let best: { el: Element; d: number } | null = null;
  for (const [name, el] of NAME_INDEX) {
    // Skip obvious mismatches by length to keep this fast.
    if (Math.abs(name.length - n.length) > budget) continue;
    const d = levenshtein(n, name, budget);
    if (d <= budget && (!best || d < best.d)) best = { el, d };
    if (best && best.d === 1) {
      // Short-circuit on a near-perfect match.
      if (n.length <= 5) break;
    }
  }
  return best?.el ?? null;
}

export function lookupElement(raw: string): Element | null {
  const n = normalize(raw);
  if (!n) return null;
  // Try aliased spelling first (US/UK)
  const aliased = SPELLING_ALIASES[n];
  if (aliased && NAME_INDEX.has(aliased)) return NAME_INDEX.get(aliased)!;
  if (NAME_INDEX.has(n)) return NAME_INDEX.get(n)!;
  // Symbol must be 1–3 chars to avoid ambiguity with name fragments
  if (n.length <= 3 && SYMBOL_INDEX.has(n)) return SYMBOL_INDEX.get(n)!;
  return null;
}

export function lastLetter(name: string): string {
  // Use the canonical name's last alphabetic character.
  const cleaned = name.replace(/[^a-zA-Z]/g, "");
  return cleaned.slice(-1).toLowerCase();
}

/**
 * Validate a move against the current round.
 *
 * @param input          Raw user input
 * @param config         Round mode + category
 * @param usedNumbers    Set of element atomic numbers already played this round
 * @param previousName   Last accepted element's canonical name (or null on first move)
 */
export function validateMove(
  input: string,
  config: RoundConfig,
  usedNumbers: ReadonlySet<number>,
  previousName: string | null,
): MoveResult {
  if (!input.trim()) {
    return { ok: false, reason: "empty", message: "Type an element name or symbol." };
  }

  let el = lookupElement(input);
  let corrected: string | undefined;
  if (!el) {
    const fuzzy = fuzzyLookupElement(input);
    if (fuzzy) {
      el = fuzzy;
      corrected = input.trim();
    }
  }
  if (!el) {
    return {
      ok: false,
      reason: "unknown",
      message: `"${input.trim()}" isn't a known element.`,
    };
  }

  if (usedNumbers.has(el.number)) {
    return {
      ok: false,
      reason: "repeat",
      message: `${el.name} has already been used this round.`,
    };
  }

  if (config.mode === "category") {
    if (!config.categoryKey) {
      return {
        ok: false,
        reason: "wrong-category",
        message: "No category selected.",
      };
    }
    const cat = getCategory(config.categoryKey);
    if (!cat.matches.includes(el.category)) {
      return {
        ok: false,
        reason: "wrong-category",
        message: `${el.name} is not a ${cat.label.toLowerCase().replace(/s$/, "")}.`,
      };
    }
  }

  if (config.mode === "last-letter" && previousName) {
    const required = lastLetter(previousName);
    const first = el.name[0]?.toLowerCase() ?? "";
    if (first !== required) {
      return {
        ok: false,
        reason: "wrong-letter",
        message: `${el.name} starts with "${first.toUpperCase()}", but you needed "${required.toUpperCase()}".`,
      };
    }
  }

  return {
    ok: true,
    element: el,
    display: `${el.name} (${el.symbol})`,
    correctedFrom: corrected,
  };
}

/** Convenience: count elements matching a category - useful for tooltips. */
export function categoryPoolSize(key: GameCategoryKey): number {
  const cat = getCategory(key);
  return elements.filter((e) => cat.matches.includes(e.category)).length;
}